import { useId, useState } from 'react'
import { cx } from './cx'
import { DecorativeDocumentSheet } from './DecorativeDocumentSheet'
import styles from './DocumentPreviewCard.module.scss'

export type DocumentPreviewCardProps = {
    /** Document name shown as the card heading. */
    title: string
    /** Short explanation of what the document is for. */
    description: string
    /** PDF (or other) URL opened in the inline preview iframe. */
    url?: string | null
    /** Label used in the show/hide preview control / iframe title. */
    actionLabel?: string
    /** Shown when `url` is missing. */
    unavailableLabel?: string
}

/**
 * Retainer / client-care document card with a decorative paper sheet in the
 * bottom-right corner and a toggleable PDF iframe preview.
 * Cloned from sentinel-web form-ui (no package import).
 */
export const DocumentPreviewCard = ({
    title,
    description,
    url,
    actionLabel = 'Document',
    unavailableLabel = 'Document not yet available',
}: DocumentPreviewCardProps) => {
    const [showPreview, setShowPreview] = useState(false)
    const previewId = useId()
    const hasUrl = Boolean(url?.trim())

    return (
        <div className={styles.root}>
            <button
                type="button"
                disabled={!hasUrl}
                aria-expanded={hasUrl ? showPreview : undefined}
                aria-controls={hasUrl ? previewId : undefined}
                onClick={() => {
                    if (!hasUrl) return
                    setShowPreview((current) => !current)
                }}
                className={cx(styles.card, showPreview && styles.cardExpanded)}
            >
                <DecorativeDocumentSheet title={title} className={styles.sheet} />

                <div className={styles.body}>
                    <h4 className={styles.title}>{title}</h4>
                    <p className={styles.description}>{description}</p>
                    {hasUrl ? (
                        <span className={cx(styles.action, showPreview && styles.actionExpanded)}>
                            <span className={showPreview ? styles.actionLabelExpanded : styles.actionLabel}>
                                {showPreview ? 'Hide Document' : 'Show Document'}
                            </span>
                        </span>
                    ) : (
                        <p className={styles.unavailable}>{unavailableLabel}</p>
                    )}
                </div>
            </button>

            {showPreview && hasUrl ? (
                <div id={previewId} className={styles.preview}>
                    <iframe title={`${actionLabel} preview`} src={url ?? undefined} className={styles.iframe} />
                </div>
            ) : null}
        </div>
    )
}
